<script lang="ts" setup>
import AdaptiveDialog from '../AdaptiveDialog.vue'
import Button from 'primevue/button'
// import Divider from 'primevue/divider'
import InputText from 'primevue/inputtext'
import Message from 'primevue/message'
import SrDataTable from '../tables/SrDataTable.vue'
import type { TableColumn } from '@/components/alerts/types'
import IftaLabel from 'primevue/iftalabel'
import SrInputNumber from '@/components/SrInputNumber.vue'
import { computed, ref, watch } from 'vue'
import { Form, FormField, type FormSubmitEvent } from '@primevue/forms'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { z } from 'zod'
import { WithdrawalsV2Service } from '@/services/withdrawals-v2-service'
import type { WithdrawalResponse } from '@/services/withdrawals-v2-service/types'
import { useMyToast } from '@/composable/useMyToast'
import { useTzDate } from '@/composable/useTzDate'
import { useAuthStore } from '@/stores/authStore'
import { i18n } from '@/i18n'
import axios from 'axios'

const MIN_AMOUNT = 50
const BEP20_ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/

const { addToast } = useMyToast()
const { dateFullStr } = useTzDate()
const { t } = i18n.global
const authStore = useAuthStore()

const visible = ref(false)
const loading = ref(false)
const submitting = ref(false)
const cancelling = ref(false)
const withdrawals = ref<WithdrawalResponse[]>([])

const activeWithdrawal = computed(() =>
  withdrawals.value.find((w) => w.status === 'pending') ?? null,
)

const availableBalance = computed(() => authStore.refBalance ?? 0)

const initialValues = {
  amount: null,
  address: '',
}

const resolver = computed(() =>
  zodResolver(
    z.object({
      amount: z.coerce
        .number()
        .min(MIN_AMOUNT, { message: 'modal.withdrawal.errors.minAmount' })
        .refine((v) => v <= availableBalance.value, {
          message: 'modal.withdrawal.errors.insufficientBalance',
        }),
      address: z
        .string()
        .regex(BEP20_ADDRESS_REGEX, { message: 'modal.withdrawal.errors.invalidAddress' }),
    }),
  ),
)

const toggle = () => {
  visible.value = !visible.value
}

defineExpose({
  toggle,
})

watch(visible, (v) => {
  if (v) {
    loadWithdrawals()
  } else {
    withdrawals.value = []
  }
})

const loadWithdrawals = async () => {
  loading.value = true
  try {
    const res = await WithdrawalsV2Service.getWithdrawals()
    withdrawals.value = res.data
  } catch (thrown) {
    handleApiError(thrown)
  } finally {
    loading.value = false
  }
}

const handleApiError = (thrown: unknown) => {
  if (axios.isAxiosError(thrown) && thrown.response) {
    const type = thrown.response.data?.detail?.[0]?.type
    if (type) {
      addToast({ severity: 'error', message: t(`modal.withdrawal.errors.${type}`) })
      return
    }
  }
  addToast({ severity: 'error', message: t('unexceptedError') })
}

const statusLabel = (status: string) => {
  const key = `modal.withdrawal.statuses.${status}`
  const translated = t(key)
  return translated === key ? status : translated
}

const submit = async (e: FormSubmitEvent) => {
  if (!e.valid) return

  const values = e.values as { amount: number; address: string }
  submitting.value = true
  try {
    await WithdrawalsV2Service.createWithdrawal({
      amount: Number(values.amount),
      address: values.address,
    })
    await authStore.check(true)
    await loadWithdrawals()
    e.reset?.()
    addToast({ severity: 'success', message: t('modal.withdrawal.created') })
  } catch (thrown) {
    handleApiError(thrown)
  } finally {
    submitting.value = false
  }
}

const cancel = async (requestId: number) => {
  cancelling.value = true
  try {
    await WithdrawalsV2Service.cancelWithdrawal(requestId)
    await authStore.check(true)
    await loadWithdrawals()
    addToast({ severity: 'success', message: t('modal.withdrawal.cancelled') })
  } catch (thrown) {
    handleApiError(thrown)
  } finally {
    cancelling.value = false
  }
}

const columns: TableColumn[] = [
  {
    field: 'created_at',
    header: 'modal.withdrawal.historyDate',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'amount',
    header: 'modal.withdrawal.amount',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'address',
    header: 'modal.withdrawal.addressOnly',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit digit' }),
  },
  {
    field: 'status',
    header: 'modal.withdrawal.status',
    class: 'text-center',
    inner: 'span',
    dynamic: true,
    innerPropsGenerator: () => ({ class: 'pit' }),
  },
]

const formattingRules = {
  created_at: (v: string) => dateFullStr(v).slice(0, -3),
  amount: (v: number | string) => `$${v}`,
  address: (v: string) => `${v.slice(0, 4)}...${v.slice(-4)}`,
  status: (v: string) => statusLabel(v),
}
</script>

<template>
  <AdaptiveDialog v-model:visible="visible" pt:root="w-150" pt:content="p-0" pt:header="px-4 py-1"
    style="overflow: hidden" :header="$t('modal.withdrawal.title')">

    <div class="p-3">


      <div v-if="activeWithdrawal" class="mb-4 p-3  rounded ">
        <div class="text-sm font-semibold mb-2">{{ $t('modal.withdrawal.activeRequest') }}</div>
        <div class="text-sm mb-1">
          <span class="font-semibold">{{ $t('modal.withdrawal.amount') }}:</span><br />
          <span class="digit">${{ activeWithdrawal.amount }}</span>
        </div>
        <div class="text-sm mb-2 break-all">
          <span class="font-semibold">{{ $t('modal.withdrawal.address') }}:</span><br />
          {{ activeWithdrawal.address }}
        </div>
        <Button size="small" severity="danger" variant="outlined" :loading="cancelling"
          @click="cancel(activeWithdrawal.id)">
          {{ $t('modal.withdrawal.cancel') }}
        </Button>
      </div>

      <Form v-else :resolver="resolver" :initial-values="initialValues" @submit="submit"
        class="grid grid-cols-1 gap-x-3 gap-y-6 mb-4">
        <FormField v-slot="$field" name="amount" class="relative">
          <IftaLabel>
            <SrInputNumber type="text" fluid :placeholder="$t('modal.withdrawal.amount')" />
            <label>{{ $t('modal.withdrawal.amount') }}</label>
          </IftaLabel>
          <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $t($field.error?.message, { min: MIN_AMOUNT }) }}
          </Message>
        </FormField>

        <FormField v-slot="$field" name="address" class="relative">
          <IftaLabel>
            <InputText type="text" fluid :placeholder="$t('modal.withdrawal.address')" autocomplete="off" />
            <label>{{ $t('modal.withdrawal.address') }}</label>
          </IftaLabel>
          <Message class="absolute" v-if="$field?.invalid" severity="error" size="small" variant="simple">
            {{ $t($field.error?.message) }}
          </Message>
        </FormField>

        <Button type="submit" severity="success" variant="outlined" :loading="submitting"
          :label="$t('modal.withdrawal.submit')" />
      </Form>
    </div>
    <!-- <Divider class="mb-0 mt-0"></Divider> -->
    <!-- <div class="text-sm font-semibold my-2 px-3">{{ $t('modal.withdrawal.history') }}</div> -->
    <SrDataTable size="small" :value="withdrawals" :columns="columns" :loading="loading"
      :formattingRules="formattingRules">
      <template #empty>
        <div class="text-center text-muted py-3">{{ $t('modal.withdrawal.historyEmpty') }}</div>
      </template>
    </SrDataTable>
  </AdaptiveDialog>
</template>
